import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import authService from '../services/authService';
import API_CONFIG from '../config/api';
import axios from 'axios';
import CardMyPost from './card/card-mypost.js';
import GnbLoginInducePanel from './gnb-login-induce-panel.js';
import { useTranslation } from '../i18n/useTranslation';
import { useWebSocket } from '../hooks/useWebSocket';
import { useCredit } from '../contexts/CreditContext';
import gigService from '../services/gigService';
import { ReactComponent as HomeIcon } from '../asset/gnb/home.svg';
import { ReactComponent as MessageIcon } from '../asset/gnb/message.svg';
import { ReactComponent as AlertIcon } from '../asset/gnb/alert.svg';
import { ReactComponent as CommunityIcon } from '../asset/gnb/community.svg';
import { ReactComponent as SelectIcon } from '../asset/gnb/select.svg';
import { ReactComponent as FindIcon } from '../asset/gnb/find.svg';
import { ReactComponent as GigStarIcon } from '../asset/gnb/GIGstar.svg';
import { ReactComponent as FolderIcon } from '../asset/gnb/folder.svg';

function GNB2({ isCollapsed = false, onToggleSidebar, isMobileOverlay = false, onCloseOverlay }) {
    const location = useLocation();
    const pathname = location.pathname;
    const { t } = useTranslation();
    const { balance: creditBalance } = useCredit();

    const MONTHLY_GRANT_BY_TIER = {
        Traveler: 0, Pioneer: 1000, Leader: 3000,
        Expert: 8000, Master: 20000, Slave: 99999,
    };
    const [userData, setUserData] = useState({
        userId: null,          // userId 초기값 추가
        nickname: 'USERNAME',
        membership: 'Slave',
        profile_image: ''
    });
    const [myPosts, setMyPosts] = useState([]);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const profileDropdownRef = useRef(null);

    // 드라이브 GIG 스페이스 목록
    const [driveSearchParams] = useSearchParams();
    const activeSpaceId = driveSearchParams.get('spaceId');
    const isDrivePath = pathname === '/drive' || pathname.startsWith('/drive');
    const [gigSpaces, setGigSpaces] = useState([]);
    const [gigSpacesLoaded, setGigSpacesLoaded] = useState(false);

    // 저장공간 사용량
    const [storageUsage, setStorageUsage] = useState({ usedBytes: 0, quotaBytes: 0, loading: false, error: null });
    
    // 알림 관련 상태
    const { isConnected, isAuthenticated, send, addEventListener, removeEventListener } = useWebSocket();
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    const messageHandlersRef = useRef({});
    const prevPathnameRef = useRef(pathname); // 이전 경로 추적

    // 미읽음 알림 개수 조회
    const fetchUnreadCount = useCallback(() => {
        if (!isConnected || !isAuthenticated) {
            return;
        }
        send({ type: 'getUnreadNotificationCount' });
    }, [isConnected, isAuthenticated, send]);

    const loadUserData = useCallback(() => {
        const data = authService.getUserData();

        if (data.nickname && data.nickname !== 'USERNAME') {
            const newUserData = {
                userId: data.userId || data.id || null,
                nickname: data.nickname,
                membership: data.membership || 'Slave',
                profile_image: data.profile_image || ''
            };
            setUserData(newUserData);
        } else if (data.nickname === 'USERNAME' && userData.nickname !== 'USERNAME') {
            setUserData({
                userId: null,
                nickname: 'USERNAME',
                membership: 'Slave',
                profile_image: ''
            });
        }
    }, [userData.nickname]);

    const fetchMyPosts = useCallback(async () => {
        if (!userData.userId) return;

        try {
            const response = await axios.get("/api/posts", {
                params: {
                    status: "mypost",
                    author_id: userData.userId,
                    only_root: true,
                    sortColumn: "timestamp",
                    sortOrder: "DESC",
                    page: 1,
                    pageSize: 20
                }
            });

            if (response.data.success) {
                const posts = response.data.data.posts || [];
                setMyPosts(posts);
            }
        } catch (err) {
            console.error("마이포스트 목록 불러오기 실패:", err);
        }
    }, [userData.userId]);

    // 알림 관련 WebSocket 핸들러 설정
    useEffect(() => {
        if (!isConnected) {
            return;
        }

        // 미읽음 개수 응답 처리
        const handleUnreadCount = (data) => {
            if (typeof data.count === 'number') {
                setUnreadNotificationCount(data.count);
            }
        };

        // 새 알림 수신 처리
        const handleNewNotification = (data) => {
            if (data.notification && !data.notification.is_read) {
                // 미읽음 알림이면 개수 증가
                setUnreadNotificationCount(prev => prev + 1);
            }
        };

        // 읽음 처리 응답
        const handleNotificationMarkRead = (data) => {
            if (data.success) {
                setUnreadNotificationCount(prev => Math.max(0, prev - 1));
            }
        };

        // 삭제 처리 응답
        const handleNotificationDeleted = (data) => {
            if (data.success) {
                // 삭제된 알림이 미읽음이었는지 확인하기 위해 개수 재조회 (페이지 이동 없이도 즉시 반영)
                fetchUnreadCount();
            }
        };

        // 메시지 핸들러 등록
        messageHandlersRef.current = {
            unreadNotificationCount: handleUnreadCount,
            newNotification: handleNewNotification,
            notificationMarkRead: handleNotificationMarkRead,
            notificationDeleted: handleNotificationDeleted
        };

        // 이벤트 리스너 등록
        Object.entries(messageHandlersRef.current).forEach(([type, handler]) => {
            addEventListener(type, handler);
        });

        // Cleanup
        return () => {
            Object.entries(messageHandlersRef.current).forEach(([type, handler]) => {
                removeEventListener(type, handler);
            });
        };
    }, [isConnected, isAuthenticated, addEventListener, removeEventListener, fetchUnreadCount]);

    // 페이지 이동 시에만 알림 체크 (리렌더링 방지)
    useEffect(() => {
        // 경로가 변경되었을 때만 알림 체크
        if (prevPathnameRef.current !== pathname) {
            prevPathnameRef.current = pathname;
            
            // 페이지 이동 시에만 미읽음 개수 조회
            if (isConnected && isAuthenticated) {
                fetchUnreadCount();
            }
        }
    }, [pathname, isConnected, isAuthenticated, fetchUnreadCount]);

    useEffect(() => {
        // 컴포넌트 마운트 시 사용자 정보 로드
        loadUserData();

        // localStorage 변경 감지 (다른 탭에서의 변경만 감지)
        const handleStorageChange = (e) => {
            if (['email', 'nickname', 'membership', 'profile_image', 'userId'].includes(e.key)) {
                loadUserData();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // 커스텀 이벤트 리스너 추가 (같은 탭에서의 변경 감지)
        const handleCustomStorageChange = () => {
            loadUserData();
        };

        window.addEventListener('userDataChanged', handleCustomStorageChange);

        // 외부 클릭 시 드롭다운 닫기
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setShowProfileDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('userDataChanged', handleCustomStorageChange);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [loadUserData]);

    useEffect(() => {
        const isMyPostRoute = pathname === '/mypost' || pathname === '/mypost-post';
        
        if (isMyPostRoute && userData.userId) {
            fetchMyPosts();
        } else if (!isMyPostRoute) {
            // 마이포스트 관련 경로가 아니면 목록 초기화
            setMyPosts([]);
        }
    }, [pathname, userData.userId, fetchMyPosts]);

    // 저장공간 사용량 조회 (로그인 시)
    useEffect(() => {
        if (!userData.userId) {
            setStorageUsage({ usedBytes: 0, quotaBytes: 0, loading: false, error: null });
            return;
        }
        setStorageUsage(prev => ({ ...prev, loading: true, error: null }));
        const url = API_CONFIG.getApiUrl ? API_CONFIG.getApiUrl('user-storage/list') : '/api/user-storage/list';
        fetch(url, { credentials: 'include' })
            .then((res) => {
                if (res.status === 401) {
                    setStorageUsage({ usedBytes: 0, quotaBytes: 0, loading: false, error: null });
                    return null;
                }
                return res.json();
            })
            .then((json) => {
                if (json === null) return;
                const used = json.usedBytes ?? 0;
                const quota = json.quotaBytes ?? 0;
                setStorageUsage({ usedBytes: used, quotaBytes: Math.max(1, quota), loading: false, error: null });
            })
            .catch(() => {
                setStorageUsage(prev => ({ ...prev, loading: false, error: true }));
            });
    }, [userData.userId]);

    // 드라이브 페이지 진입 시 내 GIG 스페이스 목록 조회
    useEffect(() => {
        if (!isDrivePath || !userData.userId || gigSpacesLoaded) return;
        gigService.getSpaces('mine', 1, 50)
            .then(res => { if (res.success) setGigSpaces(res.spaces || []); })
            .catch(() => {})
            .finally(() => setGigSpacesLoaded(true));
    }, [isDrivePath, userData.userId, gigSpacesLoaded]);

    // 로그아웃 시 초기화
    useEffect(() => {
        if (!userData.userId) {
            setGigSpaces([]);
            setGigSpacesLoaded(false);
        }
    }, [userData.userId]);

    // 포스트 삭제 성공 시 목록 새로고침
    const handleDeleteSuccess = (deletedId) => {
        setMyPosts(prev => prev.filter(post => post.id !== deletedId));
    };

    const handleLogout = () => {
        authService.logout();
        setUserData({
            userId: null,
            nickname: 'USERNAME',
            membership: 'Slave',
            profile_image: ''
        });
        window.location.href = '/';
    };

    // 멤버십별 스타일 정의
    const membershipStyles = {
        'Traveler': { backgroundColor: 'transparent', color: 'var(--color-text-3)' },
        'Pioneer': { backgroundColor: 'var(--color-warning)', color: 'inherit' },
        'Leader': { backgroundColor: 'var(--color-success)', color: 'inherit' },
        'Expert': { backgroundColor: 'var(--color-primary)', color: 'white' },
        'Master': { backgroundColor: 'var(--color-special, #8b5cf6)', color: 'white' },
        'Slave': { backgroundColor: 'var(--color-sub-2)', color: 'var(--color-primary)' },
    };

    // 현재 멤버십에 맞는 스타일 가져오기
    const currentMembershipStyle = membershipStyles[userData.membership] || membershipStyles['Traveler'];

    // 비로그인 상태 여부
    const isGuest = userData.nickname === 'USERNAME';

    // 프로필 드롭다운 토글 핸들러
    const toggleProfileDropdown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowProfileDropdown(!showProfileDropdown);
    };

    // 드롭다운 메뉴 항목 클릭 핸들러
    const handleDropdownItemClick = () => {
        setShowProfileDropdown(false);
    };

    // 저장공간 바이트 포맷
    const formatBytes = (bytes) => {
        if (bytes == null || typeof bytes !== 'number' || bytes < 0) return '0 B';
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    return (
        <div className={`box-col group-align-std gnb2-modern ${isCollapsed ? 'side-menu-narrow' : ''} ${isMobileOverlay ? 'gnb2-mobile-overlay-inner' : ''}`} id="side-menu">
            <div className="box-col pd0 gnb2-nav-group">
            {isGuest ? (
            <GnbLoginInducePanel onAction={isMobileOverlay ? onCloseOverlay : undefined} />
            ) : (
            <>
            {/* 상단: 홈, 채팅, 알람 */}
            <Link to="/" className={`side-menu-item ${pathname === '/' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <HomeIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">{t('nav.home')}</span>
            </Link>
            <Link to="/chat" className={`side-menu-item only-dt ${pathname === '/chat' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <MessageIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">{t('nav.chat')}</span>
            </Link>
            <Link to="/notice" className={`side-menu-item only-dt ${pathname === '/notice' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start', position: 'relative' }}>
                <AlertIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">{t('nav.notification')}</span>
                {unreadNotificationCount > 0 && (
                    <span 
                        className="badge-rtg badge-failure" 
                        style={{
                            position: 'absolute',
                            top: '4px',
                            right: isCollapsed ? '4px' : '8px',
                            minWidth: '18px',
                            height: '18px',
                            padding: '0 4px',
                            fontSize: '10px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '9px',
                            lineHeight: '1'
                        }}
                        title={`미확인 알림 ${unreadNotificationCount}개`}
                    >
                        {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                )}
            </Link>

            <hr className="gnb2-hr only-dt"/>

            {/* 커뮤니티 */}
            {!isCollapsed && <span className="gnb-section-label">{t('nav.community')}</span>}
            <Link to="/community" className={`side-menu-item ${pathname === '/community' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <CommunityIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">{t('nav.community')}</span>
            </Link>
            <Link to="/select" className={`side-menu-item ${pathname === '/select' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <SelectIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">{t('nav.select')}</span>
            </Link>
            <Link to="/explore" className={`side-menu-item ${pathname === '/explore' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <FindIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">{t('nav.explore')}</span>
            </Link>
            <Link to="/mypost" className={`side-menu-item ${pathname === '/mypost' || pathname === '/mypost-post' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <span className="material-symbols-rounded">article</span>
                <span className="gnb-menu-inv">{t('nav.mypost')}</span>
            </Link>

            <hr className="gnb2-hr only-dt"/>

            {/* GIG */}
            {!isCollapsed && <span className="gnb-section-label">{t('nav.gig')}</span>}
            <Link to="/gig" className={`side-menu-item ${pathname.startsWith('/gig') && !pathname.startsWith('/gig/explore') ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <GigStarIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                <span className="gnb-menu-inv">GIG</span>
            </Link>
            <Link to="/gig/explore" className={`side-menu-item ${pathname.startsWith('/gig/explore') ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <span className="material-symbols-rounded">travel_explore</span>
                <span className="gnb-menu-inv">{t('gig.home.explore')}</span>
            </Link>

            <hr className="gnb2-hr only-dt"/>

            {/* 드라이브 */}
            {isDrivePath && !isCollapsed && gigSpaces.length > 0 ? (
                <div className="gnb-drive-group">
                    <div className="side-menu-item" style={{ justifyContent: 'flex-start', pointerEvents: 'none', opacity: 0.7 }}>
                        <FolderIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                        <span className="gnb-menu-inv">{t('drive.title')}</span>
                    </div>
                    <Link
                        to="/drive"
                        className={`side-menu-item ${isDrivePath && !activeSpaceId ? 'active' : ''}`}
                        style={{ justifyContent: 'flex-start', paddingLeft: 28 }}
                    >
                        <span className="material-symbols-rounded" style={{ fontSize: 18 }}>drive_folder_upload</span>
                        <span className="gnb-menu-inv">{t('drive.myDrive')}</span>
                    </Link>
                    {gigSpaces.map(space => (
                        <Link
                            key={space.id}
                            to={`/drive?spaceId=${space.id}`}
                            className={`side-menu-item ${activeSpaceId === String(space.id) ? 'active' : ''}`}
                            style={{ justifyContent: 'flex-start', paddingLeft: 28 }}
                        >
                            <span className="material-symbols-rounded" style={{ fontSize: 18 }}>group</span>
                            <span className="gnb-menu-inv">{space.title}</span>
                        </Link>
                    ))}
                </div>
            ) : (
                <Link to="/drive" className={`side-menu-item ${pathname === '/drive' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                    <FolderIcon className="gnb-menu-svg-icon" aria-hidden="true" />
                    <span className="gnb-menu-inv">{t('drive.title')}</span>
                </Link>
            )}

            <hr className="gnb2-hr only-dt"/>

            {/* Synapse */}
            {/* <Link to="/synapse" className={`side-menu-item ${pathname === '/synapse' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <span className="material-symbols-rounded">graph_3</span>
                <span className="gnb-menu-inv">{t('nav.synapse')}</span>
            </Link> */}
            <Link to="/synapse" className={`side-menu-item ${pathname === '/synapse' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <span className="material-symbols-rounded">hub</span>
                <span className="gnb-menu-inv">{t('nav.synapse')}</span>
            </Link>
            
            {/* 스타맵: 우선 주석 처리
            <Link to="/starmap" className={`side-menu-item ${pathname === '/starmap' ? 'active' : ''}`} style={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
                <span className="material-symbols-rounded">planet</span>
                <span className="gnb-menu-inv">{t('nav.starmap')}</span>
            </Link>
            */}
            
            {/* 마이포스트 목록 (마이포스트 페이지에서만 표시) */}
            {(pathname === '/mypost' || pathname === '/mypost-post') && myPosts.length > 0 && (
                <div 
                    style={{
                        width: '100%', maxHeight: '200px',
                        paddingLeft: '12px', marginTop: '4px', 
                        overflowY: 'auto',
                    }}
                >
                    {myPosts.map((post) => (
                        <div key={post.id} style={{ width: '100%', marginBottom: '2px' }}>
                            <CardMyPost
                                id={post.id}
                                title={post.title}
                                type="post"
                                onDeleteSuccess={handleDeleteSuccess}
                            />
                        </div>
                    ))}
                </div>
            )}
            </>
            )}
            </div>

            {/* 하단 프로필 영역 */}
            {!isGuest && (
            <div className="box-col pd0" style={{ marginTop: 'auto', position: 'relative' }} ref={profileDropdownRef}>
                    <>
                        <div className="gnb-profile-block" style={{ paddingTop: '8px' }}>
                            <div 
                                className="side-menu-item" 
                                onClick={!isCollapsed ? toggleProfileDropdown : undefined}
                                style={{
                                    cursor: !isCollapsed ? 'pointer' : 'default',
                                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                                    position: 'relative'
                                }}
                            >
                                <img
                                    src={userData.profile_image ? API_CONFIG.resolveFileUrl(userData.profile_image) : API_CONFIG.getDefaultAvatar(userData.userId)}
                                    className='img-profile-round'
                                    alt="Profile"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = API_CONFIG.getDefaultAvatar(userData.userId);
                                    }}
                                />
                                {!isCollapsed && (
                                    <span className="gnb-menu-inv gnb-profile-text" style={{ flex: 1 }}>
                                        <h5 id="user-nickname">{userData.nickname}</h5>
                                        {userData.membership && (
                                            <h6 className="btn-membership" style={currentMembershipStyle}>
                                                {t(`gnb.membership.${userData.membership}`)}
                                            </h6>
                                        )}
                                    </span>
                                )}
                                {!isCollapsed && (
                                    <span className="material-symbols-rounded" style={{ marginLeft: 'auto' }}>
                                        {showProfileDropdown ? 'expand_more' : 'expand_less'}
                                    </span>
                                )}
                            </div>

                            {/* 크레딧 */}
                            {!isCollapsed && userData.userId && (() => {
                                const monthlyMax = MONTHLY_GRANT_BY_TIER[userData.membership] ?? 0;
                                const subPct = monthlyMax > 0
                                    ? Math.min(100, (creditBalance.subscription / monthlyMax) * 100)
                                    : 0;
                                const isLow = monthlyMax > 0 && subPct < 20;
                                return (
                                    <div className="gnb-storage-usage">
                                        <div className="gnb-storage-header">
                                            <span className="material-symbols-rounded gnb-storage-icon" style={{ color: 'var(--color-success)' }}>toll</span>
                                            <span>{t('gnb.credits')}</span>
                                        </div>
                                        <div
                                            role="progressbar"
                                            aria-valuenow={Math.round(subPct)}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            className="gnb-storage-progress"
                                        >
                                            <div
                                                className="gnb-storage-progress-bar"
                                                style={{
                                                    width: `${subPct}%`,
                                                    backgroundColor: isLow
                                                        ? 'var(--color-warning)'
                                                        : 'var(--color-success)',
                                                }}
                                            />
                                        </div>
                                        <div className="gnb-storage-text">
                                            <Link
                                                to="/credits"
                                                className="gnb-storage-link"
                                                onClick={handleDropdownItemClick}
                                                style={{ color: 'var(--color-success)' }}
                                            >
                                                {creditBalance.subscription.toLocaleString()}
                                            </Link>
                                            <span className="gnb-storage-total">
                                                {t('gnb.creditSubscriptionLimit', { limit: monthlyMax.toLocaleString() })}
                                                {creditBalance.activity > 0 && ` ${t('gnb.creditActivity', { count: creditBalance.activity.toLocaleString() })}`}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* 저장공간 사용량 */}
                            {!isCollapsed && userData.userId && (
                                <div className="gnb-storage-usage">
                                <div className="gnb-storage-header">
                                    <span className="material-symbols-rounded gnb-storage-icon">cloud</span>
                                    <span>{t('storage.usage')}</span>
                                </div>
                                <div
                                    role="progressbar"
                                    aria-valuenow={storageUsage.quotaBytes > 0 ? Math.min(100, Math.round((storageUsage.usedBytes / storageUsage.quotaBytes) * 100)) : 0}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                    className="gnb-storage-progress"
                                >
                                    <div
                                        className="gnb-storage-progress-bar"
                                        style={{
                                            width: `${storageUsage.quotaBytes > 0 ? Math.min(100, (storageUsage.usedBytes / storageUsage.quotaBytes) * 100) : 0}%`,
                                            backgroundColor: storageUsage.quotaBytes > 0 && (storageUsage.usedBytes / storageUsage.quotaBytes) >= 1
                                                ? 'var(--color-failure, #c00)' : 'var(--color-primary, #3b6cb5)'
                                        }}
                                    />
                                </div>
                                <div className="gnb-storage-text">
                                    {storageUsage.loading ? (
                                        <span className="gnb-storage-loading">{t('storage.usage')} …</span>
                                    ) : (
                                        <Link
                                            to="/drive"
                                            className="gnb-storage-link"
                                            onClick={handleDropdownItemClick}
                                        >
                                            {formatBytes(storageUsage.usedBytes)}
                                        </Link>
                                    )}
                                    {!storageUsage.loading && (
                                        <span className="gnb-storage-total">
                                            {' / '}
                                            {formatBytes(Math.max(1, storageUsage.quotaBytes))} {t('gnb.storageInUse')}
                                        </span>
                                    )}
                                </div>
                            </div>
                            )}
                        </div>

                        {/* 드롭다운 메뉴 */}
                        {!isCollapsed && showProfileDropdown && (
                    <div className="select-dropdown-menu select-dropdown-menu--top profile-dropdown">
                        <Link 
                            to={`/profile?userId=${userData.userId}&item=home`}
                            className="side-menu-item"
                            onClick={handleDropdownItemClick}
                            style={{
                                padding: '12px 16px',
                                textDecoration: 'none',
                                color: 'inherit',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        >
                            <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>person</span>
                            <span>{t('user.viewProfile')}</span>
                        </Link>
                        <Link
                            to="/settings"
                            className="side-menu-item"
                            onClick={handleDropdownItemClick}
                            style={{
                                padding: '12px 16px',
                                textDecoration: 'none',
                                color: 'inherit',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        >
                            <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>settings</span>
                            <span>{t('user.settings')}</span>
                        </Link>
                        {userData.membership === 'Slave' && (
                            <>
                                <Link
                                    to="/operation"
                                    className="side-menu-item"
                                    onClick={handleDropdownItemClick}
                                    style={{
                                        padding: '12px 16px',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}
                                >
                                    <span className="material-symbols-rounded" style={{ fontSize: '20px' }}>admin_panel_settings</span>
                                    <span>{t('nav.admin')}</span>
                                </Link>
                            </>
                        )}
                        <hr style={{ margin: '4px 0' }} />
                        <div 
                            className="side-menu-item"
                            onClick={(e) => {
                                e.preventDefault();
                                handleDropdownItemClick();
                                handleLogout();
                            }}
                            style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: '12px',
                                color: 'var(--color-failure)'
                            }}
                        >
                            <span className="material-symbols-rounded" style={{ fontSize: '20px', color: 'var(--color-failure)' }}>logout</span>
                            <span style={{ color: 'var(--color-failure)' }}>{t('nav.logout')}</span>
                        </div>
                    </div>
                        )}
                    </>
            </div>
            )}

            {/* 사이드바 토글 버튼 (모바일 오버레이일 때는 숨김) */}
            {!isMobileOverlay && (
                <button 
                    id={isCollapsed ? "open-gnb" : "close-gnb"}
                    className="btn-sqr btn-primary"
                    onClick={onToggleSidebar}
                    style={{
                        position: 'absolute',
                        right: '-16px',
                        bottom: '30px',
                        zIndex: 10,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <span className="material-symbols-rounded">
                        {isCollapsed ? 'chevron_right' : 'chevron_left'}
                    </span>
                </button>
            )}
        </div>
    );
}

export default GNB2;